import { useCallback } from 'react';

import { Box, Button } from '@biom3/react';
import { useStore } from 'src/context/store-context';

export type ConfirmModalOptions = {
  title?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  showConfirmButton?: boolean;
  showCancelButton?: boolean;
};

export const useConfirmModal = () => {
  const [, dispatch] = useStore();

  const closeModal = useCallback(() => {
    dispatch({ payload: { type: 'SET_TOGGLE_MODAL_OPEN', open: false } });
  }, [dispatch]);

  const openConfirmModal = useCallback(
    ({
      title = '',
      description = '',
      confirmLabel = 'Confirm',
      cancelLabel = 'Cancel',
      onConfirm,
      onCancel,
      showConfirmButton = true,
      showCancelButton = true
    }: ConfirmModalOptions) => {
      const handleCancel = () => {
        onCancel?.();
        closeModal();
      };

      const handleConfirm = () => {
        onConfirm?.();
        closeModal();
      };

      dispatch({
        payload: {
          type: 'SET_MODAL_CONTENT',
          open: true,
          content: (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'base.spacing.x4',
                padding: 'base.spacing.x6',
                maxWidth: '600px',
                backgroundColor: 'base.color.neutral.900',
                borderRadius: 'base.borderRadius.x4',
                boxShadow: 'base.shadow.5'
              }}
            >
              {title && (
                <Box
                  sx={{ margin: '0', color: '#FFFFFF', textAlign: 'center', fontWeight: 'bold' }}
                >
                  {title}
                </Box>
              )}
              {description && (
                <Box sx={{ margin: '0', color: '#FFFFFF', whiteSpace: 'pre-line' }}>
                  {description}
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  gap: 'base.spacing.x4'
                }}
              >
                {showCancelButton && (
                  <Button variant="secondary" onClick={handleCancel}>
                    {cancelLabel}
                  </Button>
                )}
                {showConfirmButton && (
                  <Button variant="primary" onClick={handleConfirm}>
                    {confirmLabel}
                  </Button>
                )}
              </Box>
            </Box>
          )
        }
      });
    },
    [dispatch, closeModal]
  );

  return { openConfirmModal };
};
